import {ProofType, Role} from '../../constants/enumerations';
import {ResultState} from '../../constants/enumerations/state.enum';
import {
  IBalanceDetail,
  IBalanceSheetItem,
  IClient,
  IDebtorItem,
  IDocumentDto,
  IHistoryDetail,
  INotificationDto,
  IReceivable,
  ISemaphoreValue,
} from '../../interfaces/dtos';
import {IBasePaymentC2P} from '../../interfaces/dtos/base-payment-c2p.interface';
import IInvoiceFeeSummary from '../../interfaces/dtos/invoice-fee-summary.interface';
import IInvoiceFee from '../../interfaces/dtos/invoice-fee.interface';
import {IStatePaymentC2p} from '../../interfaces/dtos/state-payment-c2p.interface';
import {IPaymentForm, ITaxForm} from '../../interfaces/forms';
import {IC2PPaymentFormConfirmation} from '../../interfaces/forms/c2p-payment-form-confirmation.interface';
import {IC2PPaymentForm} from '../../interfaces/forms/c2p-payment-form.interface';
import {ICreditCardPaymentForm} from '../../interfaces/forms/credit-card-payment-form.interface';
import {IFeePaymentProofForm} from '../../interfaces/forms/fee-payment-proof-form.interface';

export interface IUiConfiguration {
  proofForm: {
    payment: boolean;
    vatTax: boolean;
    incomeTax: boolean;
  };
  geoLocation: {
    interval: number;
  };
}

export interface IProfile {
  firstName: string;
  lastName: string;
  company: string;
  email: string;
  identity: string;
  entity: string;
  role: Role;
  referenceId?: string;
}

export interface IBank {
  title: string;
  value: string;
  foreign: boolean;
  comission?: {
    percent: number;
    fixed: number;
  };
}

export interface IListOption {
  title: string;
  value: string;
  source?: IBank;
}

/**
 *
 */
export interface IAuthSession {
  reset?: boolean | null;
  token?: string;
  begin?: number | null;
  lifetime?: string | null;
  signed?: boolean;
}

export interface IProductSubUnit {
  title: string;
  size: number;
}

export interface IPrice {
  value: number;
  units: {
    min: number;
    max: number;
  };
}

export interface IProductUnit {
  id: string;
  title: string;
  price: IPrice[];
  subUnit: IProductSubUnit;
}

export interface IProduct {
  id: string;
  title: string;
  summary: string;
  units: IProductUnit[];
  ivaPercent: number;
}

export interface IOrderTransport {
  driver: string;
  identity: string;
  plate: string;
  vehicle: string;
  hasTrailer: boolean;
  trailerPlate: string;
}

export interface IOrderItem {
  count: number;
  product: IProduct;
  unit: IProductUnit;
  price: number;
}

export interface IOrder {
  id: string | null;
  client: IClient;
  items: IOrderItem[] | null;
  transport: IOrderTransport | null;
  date: Date | null;
  paymentOptionId: string | null;
}

/**
 *
 */
export interface IState {
  tyc?: string;
  faq?: string;
  phone?: string;
  whatsApp?: string;
  email?: string;
  session?: IAuthSession;
  errors: any;
  entities: IListOption[];
  banks: IListOption[];
  profile: IProfile | null;
  history: IHistoryDetail[] | null;
  balances: IBalanceDetail[] | null;
  subClientBalances: IBalanceDetail[] | null;
  balanceSheet: IBalanceSheetItem | null;
  balanceSheetSubClient: IBalanceSheetItem | null;
  notifications: INotificationDto[] | null;
  uiConfiguration: IUiConfiguration | null;
  documents: IDocumentDto[] | null;
  semaphoreItems: ISemaphoreValue[] | null;
  debtors: IDebtorItem[] | null;
  receivables: IReceivable[] | null;
  isOutdated: boolean | null;
  goToMessages: boolean;
  salesmanClients: IClient[] | null;
  salesTerritories: IListOption[];
  customerTypes: IListOption[];
  customerRequiredDocumentList: IListOption[];
  customerRequiredDocumentsHelp: string | null;
  customerPaymentOptions: IListOption[];
  salesmanOrderItemCandidate: IOrderItem | null;
  salesmanClient: IClient | null;
  isLocalizationActive: boolean | null;
  invoiceFeeSummaryList: IInvoiceFeeSummary[] | null;
  invoiceFeeSummary: IInvoiceFeeSummary | null;
  invoiceFeeListToPay: IInvoiceFee[] | null;
  basePaymentC2P: IBasePaymentC2P | null;
  statePaymentC2P: IStatePaymentC2p | null;
}

/**
 *
 */
export interface IContext extends IState {
  fetchBalanceList: () => Promise<boolean>;
  fetchHistoryList: () => Promise<boolean>;
  fetchBalanceSheet: () => Promise<boolean>;
  fetchSalesmanClients: (read?: boolean) => Promise<boolean>;
  sendProof: (
    form: IPaymentForm | ITaxForm,
    type: ProofType,
  ) => Promise<boolean>;
  sendEmailBalance: (id: string, typeCode: string) => Promise<ResultState>;
  fetchSemaphoreItems: () => Promise<boolean>;
  fetchDebtorList: (key: string) => Promise<boolean>;
  fetchReceivableListByDebtor: (key: string, rif: string) => Promise<boolean>;
  actions?: typeof ACTION_VERBS;
  setSalesmanClient: (client: IClient | null) => void;
  fetchDocumentList: () => Promise<boolean>;
  fetchInvoiceFeeSummaryList: () => Promise<boolean>;
  setInvoiceFeeSummary: (invoiceFee: IInvoiceFeeSummary | null) => void;
  setInvoiceFeeListToPay: (invoiceFeeListToPay: IInvoiceFee[] | null) => void;
  fetchBasePaymentC2P: () => Promise<boolean>;
  sendPaymentC2P: (data: IC2PPaymentForm) => Promise<boolean>;
  sendProofFeePayment: (data: IFeePaymentProofForm) => Promise<boolean>;
  sendPaymentC2PConfirmation: (
    data: IC2PPaymentFormConfirmation,
  ) => Promise<boolean>;
  sendPaymentCreditCard: (form: ICreditCardPaymentForm) => Promise<boolean>;
}

/**
 *
 */
export const ACTION_VERBS = {
  BALANCE_LIST: 'BALANCE_LIST',
  NOTIFICATION_LIST: 'NOTIFICATION_LIST',
  STATE_PROCESS_ERROR: 'STATE_PROCESS_ERROR',
  SEND_PROOF: 'SEND_PROOF',
  SEND_EMAIL_BALANCE: 'SEND_EMAIL_BALANCE',
  BALANCE_SHEET: 'BALANCE_SHEET',
  BALANCE_SHEET_SUBCLIENT: 'BALANCE_SHEET_SUBCLIENT',
  BALANCE_SHEET_SALESMAN: 'BALANCE_SHEET_SALESMAN',
  HISTORY: 'HISTORY',
  SEMAPHORE_ITEMS: 'SEMAPHORE_ITEMS',
  DEBTOR_LIST: 'DEBTOR_LIST',
  RECEIVABLE_LIST: 'RECEIVABLE_LIST',
  GET_SALESMAN_CLIENTS: 'GET_SALESMAN_CLIENTS',
  GET_SALESMAN_PRODUCTS: 'GET_SALESMAN_PRODUCTS',
  SET_SALESMAN_PRODUCT_TO_ADD: 'SET_SALESMAN_PRODUCT_TO_ADD',
  UPDATE_SALESMAN_NEW_ORDER: 'UPDATE_SALESMAN_NEW_ORDER',
  UPDATE_ORDER_ITEM_CANDIDATE_SHOPPING_CART:
    'UPDATE_ORDER_ITEM_CANDIDATE_SHOPPING_CART',
  SEND_SALESMAN_NEW_ORDER: 'SEND_SALESMAN_NEW_ORDER',
  SET_SALESMAN_CLIENT: 'SET_SALESMAN_CLIENT',
  DOCUMENT_LIST: 'DOCUMENT_LIST',
  FETCH_INVOICE_FEE_SUMARY_LIST: 'FETCH_INVOICE_FEE_SUMARY_LIST',
  SET_INVOICE_FEE_SUMMARY: 'SET_INVOICE_FEE_SUMMARY',
  SET_INVOICE_FEE_LIST_TO_PAY: 'SET_INVOICE_FEE_LIST_TO_PAY',
  FETCH_BASE_PAYMENT_C2P: 'FETCH_BASE_PAYMENT_C2P',
  SEND_PAYMENT_C2P: 'SEND_PAYMENT_C2P',
  SEND_PROOF_FEE_PAYMENT: 'SEND_PROOF_FEE_PAYMENT',
  SEND_PROOF_FEE_PAYMENT_CONFIRMATION: 'SEND_PROOF_FEE_PAYMENT_CONFIRMATION',
  SEND_CREDIT_CARD_PAYMENT: 'SEND_CREDIT_CARD_PAYMENT',
};
